import { gql, useQuery } from "@apollo/client";
import { useFlags } from "launchdarkly-react-client-sdk";
import { cloneDeep } from "lodash";
import moment from "moment";
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  AddAssociatedContact,
  AddOrEditContactModal,
  ClearCustomQueueModal,
  EditCustomFieldsComponent,
  EditEmailModalV2,
  LeadsFiltersComponent,
  MergeContactsModal,
  MoveEventModal,
  SkipOrReleaseLeadModal,
  UpdateLeadDataComponent,
} from "src/Components/modal";
import DialerV2 from "src/Components/modal/DialerV2";
import { FETCH_LEAD } from "src/Components/Segments";
import { CallResultV2 } from "src/Components/Segments/CallSegments";
import CallV2 from "src/Components/Segments/CallV2";
import CallModuleSection from "src/Components/Segments/CallV2/CallModuleSection";
import { NewLeadFormV2 } from "src/Components/Segments/DashboardSideBarSegments/NewLeadFormV2";
import { SelfSourceLeadV2 } from "src/Components/Segments/DashboardSideBarSegments/SelfSourceLeadV2";
import { GeneralEventsModal } from "src/Components/Smart/MyScheduleCal";
import { AppSidebarCard, AppText, AppTextArea, FlexDiv, NewAppButton } from "src/Components/UI";
import { CallContext, LeadCardContext, ModalContext, SelfSourceLeadContext } from "src/context";
import { theme } from "src/utils/theme";
import { errorToast } from "src/utils/toast";
import styled, { keyframes } from "styled-components";
import { Calendar, DetailedLeadCard, Queue, RepKPIList } from "./";

const FETCH_EVENTS = gql`
  query fetchCalendarSchedule {
    fetchCalendarSchedule {
      id
      start_time
      end_time
      deleted_at
      what
      description
      lead_intent {
        id
        label
        type
        lead {
          id
          business_name
        }
      }
    }
  }
`;

const FETCH_GENERAL_CALLBACKS = gql`
  query fetchGeneralCallBacks($team_filter: Boolean, $org_filter: Boolean, $month: MONTH) {
    fetchGeneralCallBacks(team_filter: $team_filter, org_filter: $org_filter, month: $month)
  }
`;

type Event = {
  id: string;
  start_time: number;
  end_time: number;
  deleted_at: string | null;
  what: string;
  description?: string;
  lead_intent: {
    id: string;
    label: string;
    type: string;
    lead: {
      id: string;
      business_name: string;
    };
  };
};

export const DashboardV2: FC = () => {
  const {
    showFiltersModal,
    releaseLeadModal,
    setReleaseLeadModal,
    skipLeadModal,
    setSkipLeadModal,
    moveEventData,
    setMoveEventData,
    generalTimesEventsModal,
    showCustomFieldsModal,
    closeCustomFieldsModal,
    customFieldsData,
    showAddAssociateModal,
    setShowAddAssociateModal,
    showMergeContactsModal,
    showAddOrEditContactModal,
    setShowAddOrEditContactModal,
    showEmailEditorModal,
    selfSource,
    setSelfSource,
    setSelectedContactV2,
    openCustomFieldsModal,
    editModal,
    editModalLeadId,
    setEditModal,
    setEditModalLeadId,
    showClearCustomQueueModal,
    showDialer,
  } = useContext(ModalContext);
  const {
    callState,
    isNonCallActivity,
    callLeadId,
    callStartTime,
    phoneNumber,
    callNotesLastUpdated,
    persistCallNotes,
    callNotes,
    updateCallNotes,
  } = useContext(CallContext);
  const {
    clearAndRefetchCustomQueue,
    upcomingDials,
    selectedTabDialQueue,
    customDials,
    setUpcomingDials,
    setCustomDials,
    pinnedDials,
    loadingQueue,
  } = useContext(LeadCardContext);

  const { selfSourceLeadId, selfSourceIntentId, resetSelfSourcedLead } = useContext(SelfSourceLeadContext);

  const [selectedQueueIndex, setSelectedQueueIndex] = useState(0);
  const [highlightedLead, setHighlightedLead] = useState(null);

  const [localCallState, setLocalCallState] = useState(callState);

  const { superDialer } = useFlags();
  const { useLegacyDialer } = useContext(CallContext);

  const shouldUseLegacyFetch = useLegacyDialer || !superDialer || superDialer === undefined;

  const [showInboundLeadForm, setShowInboundLeadForm] = useState(false);

  useEffect(() => {
    if (localCallState === true && callState === false) {
      resetSelfSourcedLead();
    }
    setLocalCallState(callState);
  }, [callState]);

  /**
   * Find's the lead inside of queue and manually updates state to include new edited data.
   *
   * This is to circumvent refetching the queue just to receive the new edited data.
   */
  const editCustomFieldCallback = useCallback(
    (newCustomFieldData: any) => {
      const queueList = selectedTabDialQueue === "active" ? upcomingDials : customDials;
      const newQueueList = cloneDeep(queueList);
      newQueueList.map((item: any) => {
        if (item.lead.id !== customFieldsData.lead?.id) return item;
        item.lead.visible_custom_fields = newCustomFieldData;
        return item;
      });

      if (selectedTabDialQueue === "active") {
        setUpcomingDials(newQueueList);
      } else {
        setCustomDials(newQueueList);
      }
    },
    [upcomingDials, customDials, selectedTabDialQueue, customFieldsData],
  );

  const { data } = useQuery(FETCH_LEAD, {
    fetchPolicy: "no-cache",
    variables: { id: callLeadId },
    skip: !callLeadId,
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  useEffect(() => {
    const newInboundLead = data?.fetchLead?.newIncomingComputed;

    if (newInboundLead && !!callState && !!callLeadId) {
      setShowInboundLeadForm(true);
      return;
    }
    // protect edge cases such as user not filling out form through entire call then starting a new call.
    setShowInboundLeadForm(false);
  }, [data, callLeadId, callState]);

  const { data: eventData } = useQuery(FETCH_EVENTS, {
    fetchPolicy: "network-only",
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
      errorToast(message);
    },
  });

  const { data: generalCallBackData } = useQuery(FETCH_GENERAL_CALLBACKS, {
    fetchPolicy: "cache-and-network",
    variables: {
      month: moment().format("MMMM"),
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  const formattedEventData = useMemo(() => {
    if (!eventData?.fetchCalendarSchedule) return [];

    const allDayEvents = Object.values(generalCallBackData?.fetchGeneralCallBacks || {})
      .flat()
      .map((gcb: any) => ({
        lead_id: gcb.lead_id,
        title: gcb.business_name || "NA",
        start: moment(gcb.start).format("YYYY-MM-DDTHH:mm:ss"),
        type: "GeneralCallBack",
        allDay: true,
      }));

    // Group all-day events by date
    const groupedAllDayEvents = allDayEvents.reduce((acc: Record<string, any[]>, event) => {
      const date = moment(event.start).format("YYYY-MM-DD");
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(event);
      return acc;
    }, {});

    // Reduce groups with more than 2 events one aggregated event block
    const processedAllDayEvents = Object.entries(groupedAllDayEvents).flatMap(([_, events]) => {
      if (events.length <= 2) {
        return events;
      }
      return [
        {
          title: `${events.length} General Callbacks`,
          type: "GeneralCallBack",
          allDay: true,
          start: events[0].start,
        },
      ];
    });

    return [
      ...eventData.fetchCalendarSchedule.map((event: Event) => ({
        id: event.id,
        lead_id: event.lead_intent?.lead?.id,
        title: event.lead_intent?.lead?.business_name || "NA",
        start: moment(event.start_time).format("YYYY-MM-DDTHH:mm:ss"),
        end: moment(event.end_time).format("YYYY-MM-DDTHH:mm:ss"),
        type: event.lead_intent?.type,
        event_title: event.what,
        event_description: event.description,
      })),
      ...processedAllDayEvents,
    ];
  }, [eventData, generalCallBackData]);

  const queueItems = useMemo(
    () =>
      selectedTabDialQueue === "active"
        ? upcomingDials
        : selectedTabDialQueue === "custom"
        ? customDials
        : selectedTabDialQueue === "pinned"
        ? pinnedDials
        : [],
    [selectedTabDialQueue, upcomingDials, customDials, pinnedDials],
  );

  /**
   * Find's the lead inside of queue and manually updates state to include new edited data.
   *
   * This is to circumvent refetching the queue just to receive the new edited data.
   */
  const editLeadCallback = useCallback(
    (newLeadData: any) => {
      const queueList = selectedTabDialQueue === "active" ? upcomingDials : customDials;
      const newQueueList = cloneDeep(queueList);
      newQueueList.map((item: any) => {
        if (item.lead.id !== newLeadData.id) return item;
        Object.keys(newLeadData).forEach((key) => {
          item.lead[key] = newLeadData[key];
        });
        return item;
      });

      if (selectedTabDialQueue === "active") {
        setUpcomingDials(newQueueList);
      } else {
        setCustomDials(newQueueList);
      }
    },
    [upcomingDials, customDials, selectedTabDialQueue],
  );

  const currentSelectedLead = useMemo(() => {
    if (selfSourceIntentId) {
      return selfSourceIntentId;
    }
    return queueItems[selectedQueueIndex] || (highlightedLead as any);
  }, [selfSourceIntentId, queueItems, selectedQueueIndex, highlightedLead]);

  const [firstLoad, setFirstLoad] = useState(true);

  const shouldHideLeadDetail = useMemo(() => {
    if (selfSourceIntentId) {
      return false;
    }

    if (loadingQueue) {
      return false;
    }

    return queueItems?.length === 0 && !firstLoad;
  }, [selfSourceIntentId, queueItems, loadingQueue]);

  return (
    <BodyWrap>
      {showInboundLeadForm && (
        <NewLeadFormV2 onClose={() => setShowInboundLeadForm(false)} lead_id={callLeadId || ""} />
      )}
      {showDialer && <DialerV2 />}

      {releaseLeadModal && (
        <SkipOrReleaseLeadModal
          release
          onCancel={() => setReleaseLeadModal(false)}
          blinds={releaseLeadModal}
          setBlinds={setReleaseLeadModal}
        />
      )}
      {skipLeadModal && (
        <SkipOrReleaseLeadModal
          onCancel={() => setSkipLeadModal(false)}
          blinds={skipLeadModal}
          setBlinds={setSkipLeadModal}
        />
      )}
      {showAddAssociateModal && (
        <div style={{ position: "fixed", zIndex: 9999, width: "472px", maxHeight: "98vh", overflowY: "auto" }}>
          <AddAssociatedContact setShowAddAssociatedContact={setShowAddAssociateModal} />
        </div>
      )}
      {showAddOrEditContactModal && (
        <div style={{ position: "fixed", zIndex: 9999, width: "472px", maxHeight: "98vh", overflowY: "auto" }}>
          <AddOrEditContactModal setShowAddContact={setShowAddOrEditContactModal} />
        </div>
      )}
      {showMergeContactsModal && (
        <MergeContactModalWrapper>
          <div style={{ width: "800px", height: "600px" }}>
            <MergeContactsModal />
          </div>
        </MergeContactModalWrapper>
      )}
      {showFiltersModal && (
        <DarkDiv2>
          <SlideInDiv>
            <LeadsFiltersComponent />
          </SlideInDiv>
        </DarkDiv2>
      )}
      {generalTimesEventsModal && <GeneralEventsModal setHighlightedLead={setHighlightedLead} />}
      {moveEventData && <MoveEventModal blinds={!!moveEventData} close={() => setMoveEventData(undefined)} />}
      {showCustomFieldsModal && (
        <DarkDiv2>
          <SlideInDiv>
            <EditCustomFieldsComponent
              showEditCustomFields={showCustomFieldsModal}
              closeCustomFieldsModal={closeCustomFieldsModal}
              fields={customFieldsData.lead.visible_custom_fields}
              lead_id={customFieldsData?.lead?.id}
              callback={editCustomFieldCallback}
            />
          </SlideInDiv>
        </DarkDiv2>
      )}
      {showClearCustomQueueModal && <ClearCustomQueueModal clearAndRefetchCustomQueue={clearAndRefetchCustomQueue} />}
      {showEmailEditorModal && <EditEmailModalV2 />}
      {selfSource && (
        <>
          <div style={{ position: "absolute", zIndex: 300 }}>
            <SelfSourceLeadV2
              setSelectedContact={setSelectedContactV2}
              callState={false}
              openCustomFieldsModal={openCustomFieldsModal}
              setSelfSource={setSelfSource}
            />
          </div>
          <Blur
            onClick={() => {
              resetSelfSourcedLead();
              setSelfSource(false);
            }}
          />
        </>
      )}
      {editModal && !!editModalLeadId && (
        <DarkDiv2>
          <SlideInDiv>
            <UpdateLeadDataComponent
              visible={editModal}
              close={() => {
                setEditModal(false);
                setEditModalLeadId("");
              }}
              lead_id={editModalLeadId}
              refetchList={[]}
              callback={editLeadCallback}
            />
          </SlideInDiv>
        </DarkDiv2>
      )}

      {!callState && (
        <Queue
          selectedQueueIndex={selectedQueueIndex}
          setSelectedQueueIndex={setSelectedQueueIndex}
          highlightedLead={highlightedLead}
          setHighlightedLead={setHighlightedLead}
          firstLoad={firstLoad}
          setFirstLoad={setFirstLoad}
        />
      )}

      <ContentContainer>
        {!callState && <RepKPIList />}
        {!shouldHideLeadDetail && (
          <div style={{ height: "100%", paddingRight: "24px" }}>
            <DetailedLeadCard intentData={currentSelectedLead} />
          </div>
        )}
        <FlexDiv gap={16} style={{ height: "100%", paddingRight: "24px" }}>
          <Calendar eventData={formattedEventData} />
          {!!callState && (
            <AppSidebarCard
              style={{ border: "0px" }}
              title="My Current Call Notes"
              marginBottom="8px"
              width={432}
              render={
                <NotesInfoDiv>
                  <CallNoteLastUpdatedText
                    fontWeight={500}
                    fontSize={9}
                    style={{ color: theme.text.neutral.inverse }}
                    key={callNotesLastUpdated}
                  >
                    {!!callNotesLastUpdated && `(Updated: ${moment(callNotesLastUpdated).format("MM/DD/YYYY h:mm A")})`}
                  </CallNoteLastUpdatedText>
                  <NotesButton variant={"secondary"} onClick={() => persistCallNotes()}>
                    Save Notes
                  </NotesButton>
                </NotesInfoDiv>
              }
            >
              <CallNotesDiv>
                <NotesTextArea
                  noBorder
                  fixedSize
                  height={300}
                  width={380}
                  placeholder="Type here..."
                  value={callNotes}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                    updateCallNotes(event.target.value, callLeadId || "")
                  }
                />
              </CallNotesDiv>
            </AppSidebarCard>
          )}
        </FlexDiv>
        {(!!callState || !!isNonCallActivity) && shouldUseLegacyFetch && (
          // old version of call module (this was used as full page with top and bottom sections)
          <CallV2 callState={callState} callStartTime={callStartTime} phoneNumber={phoneNumber} />
        )}
        {(!!callState || !!isNonCallActivity) && !shouldUseLegacyFetch && (
          // only contains the call module section/modals (no top and bottom sections since that was added by super dialer)
          <CallModuleSection callState={callState} callStartTime={callStartTime} phoneNumber={phoneNumber} />
        )}
      </ContentContainer>

      {(!!callState || !!isNonCallActivity) && (
        <div style={{ position: "relative", width: "600px" }}>
          <CallResultContainer>
            <CallResultV2
              // Displays a title for the non-call activity section
              logNonCallTitle={
                isNonCallActivity
                  ? data?.fetchLead?.business_name
                    ? `${data?.fetchLead?.business_name}`
                    : data?.fetchLead?.first_name || data?.fetchLead?.last_name
                    ? `${data?.fetchLead?.first_name} ${data?.fetchLead?.last_name}`
                    : ""
                  : ""
              }
              lead_brand_name={data?.fetchLead?.brand_name}
              isInHeldPhase={data?.fetchLead?.phase === "HeldPhase"}
              lead_primary_email={data?.fetchLead?.primary_email ?? ""}
              lead_full_name={`${data?.fetchLead?.first_name} ${data?.fetchLead?.last_name}`}
              scheduled_event_behavior={data?.fetchLead?.sequence_step?.actions?.[0]?.scheduled_event_behavior}
              lead_org_name={data?.fetchLead?.organization_name}
              lead_business_name={data?.fetchLead?.business_name}
            />
          </CallResultContainer>
        </div>
      )}
    </BodyWrap>
  );
};

const MergeContactModalWrapper = styled.div`
  position: fixed;
  z-index: 9999;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const slideInAnimation = keyframes`
  0% { margin-left: 0px; width: 0px; }
  100% { margin-left: 0px; width: 552px; }
`;
const DarkDiv2 = styled.div`
  position: fixed;
  top: 0px;
  bottom: 0px;
  left: -72px;
  right: 0px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;
const SlideInDiv = styled.div`
  position: absolute;
  left: 72px;
  width: calc(478px + 72px);
  min-height: 100vh;
  overflow: hidden;
  animation-name: ${slideInAnimation};
  animation-duration: 700ms;
  z-index: 8;
  background-color: ${theme.WHITE_COLOR};
`;

const CallResultContainer = styled.div`
  position: absolute;
  right: 0;
  z-index: 2;

  width: 448px;
  height: 100vh;

  padding: 16px;

  background-color: ${theme.fill.neutral.primary};
`;

const BodyWrap = styled.div`
  display: flex;
  gap: 24px;
  height: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  width: 100%;
  height: 100%;

  padding: 16px 0px 24px 24px;
`;

const Blur = styled.div`
  position: absolute;
  z-index: 299;
  width: 100vw;
  height: 100vh;

  animation: blur 0.2s ease-in-out forwards;
  @keyframes blur {
    0% {
      background: rgba(7, 23, 68, 0);
      backdrop-filter: blur(0px);
    }
    100% {
      background: rgba(7, 23, 68, 0.4);
      backdrop-filter: blur(5px);
    }
  }
`;

const NotesInfoDiv = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CallNotesDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  cursor: text;
  height: 100%;
`;

const NotesTextArea = styled(AppTextArea)`
  padding-left: 0px;
  padding-top: 20px;
  overflow: auto;
`;

const NotesButton = styled(NewAppButton)`
  width: 80px;
  height: 12px;
  font-size: 8px;
`;

const CallNoteLastUpdatedText = styled(AppText)`
  animation: flash 0.5s ease;
  @keyframes flash {
    from {
      color: ${theme.text.brand.primary};
    }
    to {
      color: ${theme.text.neutral.inverse};
    }
  }
`;
